import { nanoid } from '@reduxjs/toolkit';

import { EWidgetRegion, EWidgetSection } from '#shared';

import { EFocusView } from '../GeoMap';
import { type RegionLayout, type TemplateSnapshot } from '../slices';
import {
  extractWidgetDefaults,
  type WidgetId,
  type WidgetInstance,
  widgetRegistry,
  type WidgetRegistry,
  type WidgetSettings,
  type WidgetType,
} from '../widgetManager';

/**
 * Helper: Build base widget layout (instances, blocks, sections)
 * This is the foundation that all templates share
 */
const buildBaseLayout = () => {
  // Helper types
  type CssSize = `${number}fr` | `${number}px` | `${number}%` | 'auto';
  type SectionsState = {
    ids: EWidgetSection[];
    entities: Record<
      string,
      {
        id: EWidgetSection;
        region: EWidgetRegion;
        direction: 'row' | 'column';
        blockOrder: WidgetId[];
        blockSizes: Record<string, CssSize>;
        sealed?: boolean;
        disabled?: boolean;
      }
    >;
  };
  type Block = {
    id: WidgetId;
    sectionId?: EWidgetSection | null;
    instanceId: WidgetId;
    instanceIndex?: number;
    visible?: boolean;
    minH?: number;
    minW?: number;
    // Additional widget instances to render alongside the primary instance
    additionalInstances?: Array<{
      instanceId: WidgetId;
      instanceIndex?: number;
      position: 'left' | 'right';
    }>;
    // Direction for additional instances layout (default: 'row')
    additionalInstancesDirection?: 'row' | 'column';
  };

  // Helper functions
  const makeInstance = <
    T extends WidgetType,
    S extends WidgetSettings<WidgetRegistry[T]['config']>,
  >({
    id,
    type,
    sectionId,
    title,
    sealed = false,
    overrides = {},
  }: {
    id?: string;
    type: T;
    sectionId: EWidgetSection | null;
    title: string;
    sealed?: boolean;
    overrides?: Partial<S>;
  }): WidgetInstance<T> => {
    const newId: WidgetId = `${type}-${id || nanoid()}`;
    const widget = widgetRegistry.get(type);
    if (!widget) {
      throw new Error(`Widget type "${String(type)}" not found in registry`);
    }
    const cfg = widget.config;
    const defaults = extractWidgetDefaults(cfg);
    return {
      id: newId,
      type,
      title,
      sealed,
      sectionId,
      settings: { ...defaults, ...overrides } as S,
    };
  };

  const makeBlock = (
    instance: WidgetInstance,
    minH?: number,
    minW?: number,
    instanceIndex?: number,
    visible?: boolean,
  ): Block => ({
    id: instance.id,
    sectionId: instance.sectionId,
    instanceId: instance.id,
    minH,
    minW,
    ...(instanceIndex !== undefined ? { instanceIndex } : {}),
    ...(visible !== undefined ? { visible } : {}),
  });

  // Build widget instances
  const widgetInstances = {
    mainNewCases: makeInstance({
      id: 'aside',
      type: 'new-cases',
      sectionId: EWidgetSection.center,
      title: 'New Cases Aside',
      sealed: true,
      overrides: {
        variant: 'vertical',
      },
    }),
    activeCases1: makeInstance({
      id: 'left-primary',
      type: 'active-cases',
      sectionId: EWidgetSection.leftPrimary,
      title: 'Active Cases 1',
      overrides: {
        shareSettings: true,
      },
    }),
    activeCases2: makeInstance({
      id: 'right-primary',
      type: 'active-cases',
      sectionId: EWidgetSection.rightPrimary,
      title: 'Active Cases 2',
      overrides: {
        shareSettings: true,
      },
    }),
    newCases1: makeInstance({
      id: 'panel',
      type: 'new-cases',
      sectionId: null,
      title: 'New Cases',
      overrides: {},
    }),
    mainGeoMap: makeInstance({
      id: 'main',
      type: 'geo-map',
      sectionId: EWidgetSection.center,
      title: 'Main Geo Map',
      sealed: true,
      overrides: {
        initialFocus: EFocusView.US_CONTINENTAL,
      },
    }),
    geoMap1: makeInstance({
      id: 'focus-1',
      type: 'geo-map',
      sectionId: null,
      title: 'Focus View 1',
      overrides: {
        initialFocus: EFocusView.US_NORTHEAST,
      },
    }),
    geoMap2: makeInstance({
      id: 'focus-2',
      type: 'geo-map',
      sectionId: null,
      title: 'Focus View 2',
      overrides: {
        initialFocus: EFocusView.US_SOUTHEAST,
      },
    }),
    geoMap3: makeInstance({
      id: 'focus-3',
      type: 'geo-map',
      sectionId: null,
      title: 'Focus View 3',
      overrides: {
        initialFocus: EFocusView.US_MIDWEST,
      },
    }),
    geoMap4: makeInstance({
      id: 'focus-4',
      type: 'geo-map',
      sectionId: null,
      title: 'Focus View 4',
      overrides: {
        initialFocus: EFocusView.US_SOUTH,
      },
    }),
    windMap: makeInstance({
      id: 'panel',
      type: 'wind-map',
      sectionId: null,
      title: 'Wind Map',
      overrides: {},
    }),
    windMapStandalone: makeInstance({
      id: 'standalone-main',
      type: 'wind-map',
      sectionId: null,
      title: 'Wind Map Standalone Main',
      overrides: {
        initialFocus: EFocusView.US_CONTINENTAL,
        legendExtraEnabled: true,
      },
      sealed: true,
    }),
    windMapStandaloneAlaska: makeInstance({
      id: 'standalone-alaska',
      type: 'wind-map',
      sectionId: null,
      title: 'Wind Map Standalone Secondary Alaska',
      overrides: {
        initialFocus: EFocusView.US_ALASKA,
        legendExtraEnabled: false,
      },
      sealed: true,
    }),
    windMapStandaloneHawaii: makeInstance({
      id: 'standalone-hawaii',
      type: 'wind-map',
      sectionId: null,
      title: 'Wind Map Standalone Secondary Hawaii',
      overrides: {
        initialFocus: EFocusView.US_HAWAII,
        legendExtraEnabled: false,
      },
      sealed: true,
    }),
    windMapStandalonePuertoRico: makeInstance({
      id: 'standalone-puerto-rico',
      type: 'wind-map',
      sectionId: null,
      title: 'Wind Map Standalone Secondary Puerto Rico',
      overrides: {
        initialFocus: EFocusView.US_PUERTO_RICO,
        legendExtraEnabled: false,
      },
      sealed: true,
    }),
    airfleetTracker: makeInstance({
      id: 'panel',
      type: 'airfleet-tracker',
      sectionId: null,
      title: 'Airfleet Tracker',
      overrides: {},
    }),
    hubsReport: makeInstance({
      id: 'panel',
      type: 'hubs-report',
      sectionId: null,
      title: 'Hubs Report',
      overrides: {},
    }),
    crewAvailability: makeInstance({
      id: 'panel',
      type: 'crew-availability',
      sectionId: null,
      title: 'Crew Availability',
      overrides: {},
    }),
    statistics: makeInstance({
      id: 'panel',
      type: 'statistics',
      sectionId: null,
      title: 'Statistics',
      overrides: {},
    }),
    systemMonitor: makeInstance({
      id: 'panel',
      type: 'system-monitor',
      sectionId: null,
      title: 'System Monitor',
      overrides: {},
    }),
    airfleetTrackerPage: makeInstance({
      id: 'page',
      type: 'airfleet-tracker',
      sectionId: null,
      title: 'Airfleet Tracker',
      overrides: {},
      sealed: true,
    }),
    hubsReportPage: makeInstance({
      id: 'page',
      type: 'hubs-report',
      sectionId: null,
      title: 'Hubs Report',
      overrides: {},
      sealed: true,
    }),
    crewAvailabilityPage: makeInstance({
      id: 'page',
      type: 'crew-availability',
      sectionId: null,
      title: 'Crew Availability',
      overrides: {},
      sealed: true,
    }),
    statisticsPage: makeInstance({
      id: 'page',
      type: 'statistics',
      sectionId: null,
      title: 'Statistics',
      overrides: {},
      sealed: true,
    }),
    systemMonitorPage: makeInstance({
      id: 'page',
      type: 'system-monitor',
      sectionId: null,
      title: 'System Monitor',
      overrides: {},
      sealed: true,
    }),
  } satisfies Record<string, WidgetInstance>;

  // Build sections
  const sections: SectionsState['entities'] = {
    [EWidgetSection.leftPrimary]: {
      id: EWidgetSection.leftPrimary,
      region: EWidgetRegion.Left,
      direction: 'column',
      blockOrder: [],
      blockSizes: {},
    },
    [EWidgetSection.center]: {
      id: EWidgetSection.center,
      region: EWidgetRegion.Center,
      direction: 'column',
      blockOrder: [],
      blockSizes: {},
      sealed: true,
    },
    [EWidgetSection.rightPrimary]: {
      id: EWidgetSection.rightPrimary,
      region: EWidgetRegion.Right,
      direction: 'column',
      blockOrder: [],
      blockSizes: {},
      sealed: false,
    },
  };

  // Detect screen size
  const isXlScreen = window.matchMedia('(min-width: 1920px)').matches;

  // Build blocks
  const blocks = {
    activeCases1: makeBlock(widgetInstances.activeCases1, 150),
    activeCases2: makeBlock(widgetInstances.activeCases2, 150),
    newCases1: makeBlock(widgetInstances.newCases1),
    // Main geomap block now includes newCases asides as additional instances
    // Direction defaults to 'row' for horizontal layout (left | center | right)
    // Can be set to 'column' for vertical layout (top / center / bottom)
    mainGeoMap: {
      ...makeBlock(widgetInstances.mainGeoMap),
      additionalInstances: isXlScreen
        ? [
            {
              instanceId: widgetInstances.mainNewCases.id,
              instanceIndex: 0,
              position: 'left' as const,
            },
            {
              instanceId: widgetInstances.mainNewCases.id,
              instanceIndex: 1,
              position: 'right' as const,
            },
          ]
        : [],
      additionalInstancesDirection: 'row', // Optional: 'row' (default) or 'column'
    },
    geoMap1: makeBlock(widgetInstances.geoMap1, 220),
    geoMap2: makeBlock(widgetInstances.geoMap2, 220),
    geoMap3: makeBlock(widgetInstances.geoMap3, 220),
    geoMap4: makeBlock(widgetInstances.geoMap4, 220),
    windMap: makeBlock(widgetInstances.windMap, 240),
    windMapStandalone: makeBlock(widgetInstances.windMapStandalone),
    windMapStandaloneAlaska: makeBlock(widgetInstances.windMapStandaloneAlaska),
    windMapStandaloneHawaii: makeBlock(widgetInstances.windMapStandaloneHawaii),
    windMapStandalonePuertoRico: makeBlock(
      widgetInstances.windMapStandalonePuertoRico,
    ),
    airfleetTracker: makeBlock(widgetInstances.airfleetTracker),
    hubsReport: makeBlock(widgetInstances.hubsReport),
    crewAvailability: makeBlock(widgetInstances.crewAvailability),
    statistics: makeBlock(widgetInstances.statistics),
    systemMonitor: makeBlock(widgetInstances.systemMonitor),
    airfleetTrackerPage: makeBlock(widgetInstances.airfleetTrackerPage),
    hubsReportPage: makeBlock(widgetInstances.hubsReportPage),
    crewAvailabilityPage: makeBlock(widgetInstances.crewAvailabilityPage),
    statisticsPage: makeBlock(widgetInstances.statisticsPage),
    systemMonitorPage: makeBlock(widgetInstances.systemMonitorPage),
  } satisfies Record<string, Block>;

  // Assign blocks to sections
  sections[EWidgetSection.leftPrimary].blockOrder = [blocks.activeCases1.id];
  sections[EWidgetSection.leftPrimary].blockSizes = {
    [blocks.activeCases1.id]: '1fr' as CssSize,
  };
  // Center section now has only mainGeoMap block with embedded newCases asides
  sections[EWidgetSection.center].blockOrder = [blocks.mainGeoMap.id];
  sections[EWidgetSection.center].blockSizes = {
    [blocks.mainGeoMap.id]: '1fr' as CssSize,
  };
  sections[EWidgetSection.rightPrimary].blockOrder = [blocks.activeCases2.id];
  sections[EWidgetSection.rightPrimary].blockSizes = {
    [blocks.activeCases2.id]: '1fr' as CssSize,
  };

  // Detect screen size
  const isBelowMd = window.matchMedia('(max-width: 1023px)').matches;

  // Build regions
  const regions: RegionLayout[] = [
    {
      id: EWidgetRegion.Left,
      sectionOrder: [sections[EWidgetSection.leftPrimary].id],
      sectionSizes: {
        [sections[EWidgetSection.leftPrimary].id]: '1fr' as CssSize,
      },
      enabled: !isBelowMd,
      hidden: isBelowMd,
    },
    {
      id: EWidgetRegion.Center,
      sectionOrder: [sections[EWidgetSection.center].id],
      sectionSizes: {
        [sections[EWidgetSection.center].id]: '1fr' as CssSize,
      },
      enabled: true,
      hidden: false,
    },
    {
      id: EWidgetRegion.Right,
      sectionSizes: {
        [sections[EWidgetSection.rightPrimary].id]: '1fr' as CssSize,
      },
      sectionOrder: [sections[EWidgetSection.rightPrimary].id],
      enabled: isXlScreen,
      hidden: !isXlScreen,
    },
  ];

  // Build final sections state
  const sectionsState = {
    ids: [
      sections[EWidgetSection.leftPrimary].id,
      sections[EWidgetSection.center].id,
      sections[EWidgetSection.rightPrimary].id,
    ] as EWidgetSection[],
    entities: {
      [sections[EWidgetSection.leftPrimary].id]:
        sections[EWidgetSection.leftPrimary],
      [sections[EWidgetSection.center].id]: sections[EWidgetSection.center],
      [sections[EWidgetSection.rightPrimary].id]:
        sections[EWidgetSection.rightPrimary],
    },
  };

  // Build blocks state
  const blocksState = {
    ids: Object.keys(blocks).map(key => blocks[key as keyof typeof blocks].id),
    entities: Object.fromEntries(
      Object.entries(blocks).map(([_, block]) => [block.id, block]),
    ),
  };

  // Build widget instances state
  const widgetInstancesState = {
    ids: Object.keys(widgetInstances).map(
      key => widgetInstances[key as keyof typeof widgetInstances].id,
    ),
    entities: Object.fromEntries(
      Object.entries(widgetInstances).map(([_, instance]) => [
        instance.id,
        instance,
      ]),
    ),
  };

  return {
    regions,
    sections: sectionsState,
    blocks: blocksState,
    widgetInstances: widgetInstancesState,
  };
};

/**
 * Helper: Create region configuration for templates
 * @param config Configuration object specifying which regions are enabled
 */
const createRegionConfig = (config: {
  leftEnabled: boolean;
  centerEnabled: boolean;
  rightEnabled: boolean;
}): RegionLayout[] => {
  const { leftEnabled, centerEnabled, rightEnabled } = config;

  return [
    {
      id: EWidgetRegion.Left,
      sectionOrder: [EWidgetSection.leftPrimary],
      sectionSizes: {
        [EWidgetSection.leftPrimary]: '1fr',
      },
      enabled: leftEnabled,
      hidden: !leftEnabled,
    },
    {
      id: EWidgetRegion.Center,
      sectionOrder: [EWidgetSection.center],
      sectionSizes: {
        [EWidgetSection.center]: '1fr',
      },
      enabled: centerEnabled,
      hidden: !centerEnabled,
    },
    {
      id: EWidgetRegion.Right,
      sectionOrder: [EWidgetSection.rightPrimary],
      sectionSizes: {
        [EWidgetSection.rightPrimary]: '1fr',
      },
      enabled: rightEnabled,
      hidden: !rightEnabled,
    },
  ];
};

/**
 * Helper: Create a template snapshot
 * @param name Template name
 * @param regions Region configuration
 * @param baseLayout Base layout containing sections, blocks, and widget instances
 * @param isCustom Custom template - modifiable
 * @param sectionOverrides Optional overrides for section blockOrder and blockSizes
 */
const createTemplateSnapshot = (
  name: string,
  regions: RegionLayout[],
  baseLayout: ReturnType<typeof buildBaseLayout>,
  isCustom = false,
  sectionOverrides?: Record<
    string,
    { blockOrder: WidgetId[]; blockSizes: Record<WidgetId, string> }
  >,
): TemplateSnapshot => {
  // Apply section overrides if provided
  const sections = sectionOverrides
    ? Object.values(baseLayout.sections.entities).map(section => ({
        ...section,
        ...(sectionOverrides[section.id] ?? {}),
      }))
    : Object.values(baseLayout.sections.entities);

  // Use stable template ID based on name (slugified) for system templates
  // This ensures IDs are consistent across app restarts for proper persistence
  const stableId = isCustom
    ? 'custom-template'
    : `template-${name.toLowerCase().replace(/\s+/g, '-')}`;

  return {
    id: stableId,
    name,
    isCustom,
    regions,
    sections: sections as TemplateSnapshot['sections'],
    blocks: baseLayout.blocks.entities,
    widgetInstances: baseLayout.widgetInstances.entities,
  };
};

/**
 * Build default system templates
 * These templates are pre-configured layouts that users can select
 *
 * Alpha is the default template that all users start with.
 *
 * @returns Object containing:
 *  - templates: Array of all default templates
 *  - defaultLayout: Alpha template structure ready for Redux state
 *  - defaultTemplateMetadata: Alpha template info (id, name, isCustom)
 */
export const buildDefaultTemplates = () => {
  const baseLayout = buildBaseLayout();

  // Helper to find block id by widget instance title
  const findBlockIdByTitle = (title: string): WidgetId => {
    const block = Object.values(baseLayout.blocks.entities).find(
      b => baseLayout.widgetInstances.entities[b.instanceId]?.title === title,
    );
    return (block?.id ?? '') as WidgetId;
  };

  // Block IDs for template configuration
  const activeCases1BlockId = findBlockIdByTitle('Active Cases 1');
  const activeCases2BlockId = findBlockIdByTitle('Active Cases 2');
  const focusView1BlockId = findBlockIdByTitle('Focus View 1');
  const focusView2BlockId = findBlockIdByTitle('Focus View 2');
  const focusView3BlockId = findBlockIdByTitle('Focus View 3');
  const mainGeoMapBlockId = findBlockIdByTitle('Main Geo Map');
  const airfleetTrackerBlockId = findBlockIdByTitle('Airfleet Tracker');
  const statisticsBlockId = findBlockIdByTitle('Statistics');
  const systemMonitorBlockId = findBlockIdByTitle('System Monitor');
  const windMapBlockId = findBlockIdByTitle('Wind Map');
  const crewAvailabilityBlockId = findBlockIdByTitle('Crew Availability');
  const newCasesBlockId = findBlockIdByTitle('New Cases');

  // Template 1: Alpha (Left: Active Cases | Center: Geo Map | Right: Active Cases + Focus View)
  const alphaTemplate = createTemplateSnapshot(
    'Alpha',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [activeCases1BlockId],
        blockSizes: {
          [activeCases1BlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [activeCases2BlockId, focusView1BlockId],
        blockSizes: {
          [activeCases2BlockId]: '1fr',
          [focusView1BlockId]: '1fr',
        },
      },
    },
  );

  // Template 2: Bravo (Left: Active Cases + Focus View | Center: Geo Map | Right: Active Cases + Focus View)
  const bravoTemplate = createTemplateSnapshot(
    'Bravo',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [activeCases1BlockId, focusView1BlockId],
        blockSizes: {
          [activeCases1BlockId]: '1fr',
          [focusView1BlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [activeCases2BlockId, focusView2BlockId],
        blockSizes: {
          [activeCases2BlockId]: '1fr',
          [focusView2BlockId]: '1fr',
        },
      },
    },
  );

  // Template 3: TV Alpha
  // Left: Active Cases + Air Fleet Tracker | Center: Geo Map | Right: AC + FV + FV + Stats
  const tvAlphaTemplate = createTemplateSnapshot(
    'TV Alpha',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [activeCases1BlockId, airfleetTrackerBlockId],
        blockSizes: {
          [activeCases1BlockId]: '1fr',
          [airfleetTrackerBlockId]: '1fr',
        },
      },
      [EWidgetSection.center]: {
        blockOrder: [mainGeoMapBlockId, windMapBlockId],
        blockSizes: {
          [mainGeoMapBlockId]: '1fr',
          [windMapBlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [
          activeCases2BlockId,
          focusView1BlockId,
          focusView2BlockId,
          statisticsBlockId,
        ],
        blockSizes: {
          [activeCases2BlockId]: '1fr',
          [focusView1BlockId]: '1fr',
          [focusView2BlockId]: '1fr',
          [statisticsBlockId]: '1fr',
        },
      },
    },
  );

  // Template 4: TV Bravo
  const tvBravoTemplate = createTemplateSnapshot(
    'TV Bravo',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [
          activeCases1BlockId,
          focusView1BlockId,
          airfleetTrackerBlockId,
          crewAvailabilityBlockId,
        ],
        blockSizes: {
          [activeCases1BlockId]: '1fr',
          [airfleetTrackerBlockId]: '1fr',
        },
      },
      [EWidgetSection.center]: {
        blockOrder: [mainGeoMapBlockId, windMapBlockId],
        blockSizes: {
          [mainGeoMapBlockId]: '1fr',
          [windMapBlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [
          activeCases2BlockId,
          focusView2BlockId,
          focusView3BlockId,
          statisticsBlockId,
        ],
        blockSizes: {
          [activeCases2BlockId]: '1fr',
          [focusView2BlockId]: '1fr',
          [statisticsBlockId]: '1fr',
        },
      },
    },
  );

  // Template 5: Aviation (Left: New Cases + Active Cases | Center: Air Fleet Tracker + Wind Map | Right: Crew monitor + Geo map)
  const aviationTemplate = createTemplateSnapshot(
    'Aviation',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [newCasesBlockId, activeCases1BlockId],
        blockSizes: {
          [newCasesBlockId]: '1fr',
          [activeCases1BlockId]: '1fr',
        },
      },
      [EWidgetSection.center]: {
        blockOrder: [airfleetTrackerBlockId, windMapBlockId],
        blockSizes: {
          [airfleetTrackerBlockId]: '1fr',
          [windMapBlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [crewAvailabilityBlockId, focusView1BlockId],
        blockSizes: {
          [crewAvailabilityBlockId]: '1fr',
          [focusView1BlockId]: '1fr',
        },
      },
    },
  );

  // Template 6: Fullscreen (Center only - Hubs Report)
  const hubsReportBlockId = findBlockIdByTitle('Hubs Report');
  const _fullscreenTemplate = createTemplateSnapshot(
    'Fullscreen',
    createRegionConfig({
      leftEnabled: false,
      centerEnabled: true,
      rightEnabled: false,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [],
        blockSizes: {},
      },
      [EWidgetSection.center]: {
        blockOrder: [hubsReportBlockId],
        blockSizes: {
          [hubsReportBlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [],
        blockSizes: {},
      },
    },
  );

  // Template 7: Case Cards (Center only: New Cases + Active Cases stacked)
  const caseCardsTemplate = createTemplateSnapshot(
    'Case Cards',
    createRegionConfig({
      leftEnabled: false,
      centerEnabled: true,
      rightEnabled: false,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [],
        blockSizes: {},
      },
      [EWidgetSection.center]: {
        blockOrder: [newCasesBlockId, activeCases1BlockId],
        blockSizes: {
          [newCasesBlockId]: '1fr',
          [activeCases1BlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [],
        blockSizes: {},
      },
    },
  );

  // Template 8: Tech
  // Left: AC + Air Fleet Tracker | Center: Geo Map + Sys Mon | Right: AC + FV + Wind Map + Crew
  const techTemplate = createTemplateSnapshot(
    'Tech',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    false,
    {
      [EWidgetSection.leftPrimary]: {
        blockOrder: [activeCases1BlockId, airfleetTrackerBlockId],
        blockSizes: {
          [activeCases1BlockId]: '1fr',
          [airfleetTrackerBlockId]: '1fr',
        },
      },
      [EWidgetSection.center]: {
        blockOrder: [mainGeoMapBlockId, systemMonitorBlockId],
        blockSizes: {
          [mainGeoMapBlockId]: '1fr',
          [systemMonitorBlockId]: '1fr',
        },
      },
      [EWidgetSection.rightPrimary]: {
        blockOrder: [
          activeCases2BlockId,
          focusView1BlockId,
          windMapBlockId,
          crewAvailabilityBlockId,
        ],
        blockSizes: {
          [activeCases2BlockId]: '1fr',
          [focusView1BlockId]: '1fr',
          [windMapBlockId]: '1fr',
          [crewAvailabilityBlockId]: '1fr',
        },
      },
    },
  );

  // Template 9: Custom (Editable - starts with Alpha layout)
  const customTemplate = createTemplateSnapshot(
    'Custom',
    createRegionConfig({
      leftEnabled: true,
      centerEnabled: true,
      rightEnabled: true,
    }),
    baseLayout,
    true,
  );

  const templates = [
    alphaTemplate,
    bravoTemplate,
    tvAlphaTemplate,
    tvBravoTemplate,
    aviationTemplate,
    // fullscreenTemplate, temporarily disable fullscreen template
    caseCardsTemplate,
    techTemplate,
    customTemplate,
  ];

  // Build sections state from alphaTemplate (not baseLayout) to match Alpha's configuration
  const alphaSectionsEntities = Object.fromEntries(
    alphaTemplate.sections.map(section => [section.id, section]),
  ) as Record<EWidgetSection, (typeof alphaTemplate.sections)[number]>;

  return {
    templates,
    // Pre-built default layout (Alpha) ready for Redux state
    defaultLayout: {
      regions: alphaTemplate.regions,
      sections: {
        ids: baseLayout.sections.ids,
        entities: alphaSectionsEntities,
      },
      blocks: baseLayout.blocks,
      widgetInstances: baseLayout.widgetInstances,
    },
    // Default template metadata
    defaultTemplateMetadata: {
      id: alphaTemplate.id,
      name: alphaTemplate.name,
      isCustom: alphaTemplate.isCustom,
    },
  };
};
